import React from 'react';
import { Field, FieldArray } from 'redux-form';
import { months } from '../../../../Utils/months';
import { 
    renderCollapsibleSectionHeader,
    renderDifferenceRow,
    formatComparativoCurrency,
    useComparativoTotalsLocal,
} from '../Utils/ComparativoFlujoCajaComponents';
import { TYPE_OF_FLOW } from '../../../../../../utility/constants';
import { renderCurrency, renderDollar, renderField, renderFieldDisabled } from '../../../../Utils/renderField/renderField';
import Swal from 'sweetalert2';
const alerta = require('assets/img/icons/alerta_roja.png');

const TableRow = ({ system, index, fields, change, currency, ...props }) => {
    const currentField = fields.get(index);
    const isManual = currentField.is_manual;
    const isSalida = currentField.type_of_flow === TYPE_OF_FLOW.SALIDAS;
    
    const calculateTotalForRow = () => {
        const systemData = fields.get(index);
        if (!systemData) return 0;

        let totalForRow = months.reduce((total, month) => {
            const value = parseFloat(systemData[month]) || 0;
            return total + value;
        }, 0);
        
        fields.get(index).total = totalForRow;
        return totalForRow;
    };

    const getRenderField = () => {
        return currency === 'USD' ? renderDollar : renderCurrency;

    };

    const eliminarFilaManual = () => {
        if (isManual) {
            const currentRow = fields.get(index);
            const isNew = currentRow.isNew;
            
            if (isNew) {
                // Si es nuevo, solo quitar del FieldArray
                fields.remove(index);
            } else if (currentRow.id && props.eliminarRegistro) {
                // Si tiene ID (está guardado), mostrar confirmación y eliminar de la base de datos
                const SwalMod = Swal.mixin({
                    customClass: {
                        confirmButton: 'btn btn-primary btn-modal',
                        cancelButton: 'btn btn-outline-dark mr-5 btn-modal',
                        content: '',
                        title: 'darck',
                        popup: 'custom-modal-size',
                    },
                    buttonsStyling: false,
                    showCloseButton: true,
                    focusConfirm: false,
                    focusCancel: false,
                    padding: "1em",
                });

                SwalMod.fire({
                    title: 'ADVERTENCIA',
                    html: 'Si eliminas el registro perderás toda la información.<br/>¿Estás seguro de continuar?',
                    imageUrl: alerta,
                    imageAlt: 'Alerta',
                    showCancelButton: true,
                    confirmButtonText: 'CONTINUAR',
                    cancelButtonText: 'CANCELAR',
                    reverseButtons: true,
                }).then((result) => {
                    if (result.value) {
                        // Solo eliminar de DB - el dispatch(read()) se encarga de refrescar el formulario
                        props.eliminarRegistro(currentRow.id);
                    }
                });
            } else {
                // Fallback: quitar del FieldArray
                fields.remove(index);
            }
        }
    };

    const getTextColor = () => {
        return isSalida ? { color: '#dc3545' } : {};
    };

    React.useEffect(() => {
        change(`${system}.is_dollar`, currency === 'USD');
    }, [currency, system, change]);

    return (
        <React.Fragment>
            <tr>
                <td className='first-column table-form-fixed-cell-left-0'>
                    <Field
                        name={`${system}.name`}
                        type="text"
                        component={isManual ? renderField : renderFieldDisabled}
                        className="form-control"
                        placeholder='Nuevo'
                        style={getTextColor()}
                    />
                </td>
                <td className='table-form-fixed-cell-left-1 text-right-padded' style={getTextColor()}>
                    {formatComparativoCurrency(calculateTotalForRow(), currency, calculateTotalForRow() < 0)}
                </td>

                {months.map((month) => {
                    return (
                        <td key={month}>
                            <Field
                                name={`${system}.${month}`}
                                type="text"
                                component={getRenderField()}
                                placeholder={fields.get(index).is_dollar ? '$ 0.00' : 'Q 0.00'}
                                isSalida={isSalida}
                                onBlur={() => {
                                    calculateTotalForRow();
                                }}
                            />
                        </td>
                    );
                })}

                <td className="text-center sin-borde-top">
                    {isManual &&
                        <div>
                            <img src={require('assets/img/icons/Accion-Eliminar.svg')} alt="Borrar"
                                title="Borrar" className="action_img"
                                onClick={eliminarFilaManual}
                            />
                        </div>
                    }
                </td>
            </tr>
        </React.Fragment>
    );
};

const GenericTable = (props) => {
    const { fields, currency, allValues } = props;

    const [collapsed, setCollapsed] = React.useState(false);
    
    // Usar cálculos locales
    const localTotals = useComparativoTotalsLocal(allValues);

    // Filtrar inversiones usando la misma lógica que operativos
    const allInvestments = fields.getAll() || [];
    const validInvestments = allInvestments
        .map((field, index) => ({ field, index }))
        .filter(({ field }) => {
            // Filtrar títulos y registros inválidos
            if (field.isTitulo) return false;
            
            // Solo mostrar registros que tienen datos válidos o son nuevos
            const hasValidData = field.name || field.isNew || months.some(month => (parseFloat(field[month]) || 0) !== 0);
            return hasValidData;
        });

    // Calcular total neto de inversiones
    const totalInversiones = localTotals ? localTotals.inversionesNeto.total : 0;

    // Función para agregar nueva inversión
    const agregarInversion = () => {
        fields.push({
            isNew: true,
            is_manual: true,
            is_dollar: true,
            name: '',
            type_of_row: 3, // RegistroComparativoFlujoCaja.INVESTMENTS
            activo: true,
            type_of_flow: TYPE_OF_FLOW.SALIDAS,
            ...months.reduce((acc, month) => ({ ...acc, [month]: 0 }), {})
        });
        setCollapsed(false);
    };

    return (
        <div className=" col-sm-12 form-group np-r p-0">
            <div className='table-responsive'>
                <table className="table mb-0 table-striped">
                    {/* Header principal colapsable */}
                    {renderCollapsibleSectionHeader(
                        'FLUJOS DE INVERSIÓN',
                        '#fff3cd',
                        '#856404',
                        [],
                        collapsed,
                        () => setCollapsed(!collapsed),
                        totalInversiones
                    )}
                    
                    <tbody style={{ backgroundColor: "#F0FFC7" }}>
                        {!collapsed && (
                            <React.Fragment>
                                {/* Renderizar inversiones filtradas */}
                                {validInvestments.map(({ field, index }) =>
                                    <TableRow
                                        key={`investment-${index}`}
                                        system={`investments.${index}`}
                                        index={index}
                                        fields={fields}
                                        change={props.change}
                                        currency={currency}
                                        {...props}
                                    />
                                )}

                                {/* FILA DE TOTALES */}
                                {renderDifferenceRow(
                                    'TOTAL',
                                    totalInversiones,
                                    localTotals ? localTotals.inversionesNeto : {},
                                    currency
                                )}
                            </React.Fragment>
                        )}
                        
                        {/* Mostrar solo total cuando está colapsado */}
                        {collapsed && (
                            <React.Fragment>
                                {renderDifferenceRow(
                                    'TOTAL',
                                    totalInversiones,
                                    localTotals ? localTotals.inversionesNeto : {},
                                    currency
                                )}
                            </React.Fragment>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Botón para agregar nueva inversión */}
            {!collapsed && (
                <div className="mt-3 mb-3">
                    <div className="d-flex justify-content-center">
                        <button 
                            type="button" 
                            className="btn btn-outline-warning btn-lg"
                            onClick={agregarInversion}
                            style={{ 
                                borderColor: '#dc3545',
                                color: '#dc3545',
                                backgroundColor: 'transparent',
                                transition: 'all 0.3s ease',
                                minWidth: '300px',
                                fontWeight: 'bold'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dc3545';
                                e.target.style.color = 'white';
                                e.target.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#dc3545';
                                e.target.style.transform = 'scale(1)';
                            }}>
                            + AGREGAR INVERSIÓN
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const FlujosInversionTable = (props) => {
    const { dollar_exchange, allValues, change, eliminarRegistro, currency } = props;

    return (
        <div>
            <FieldArray
                name={`investments`}
                dollar_exchange={dollar_exchange}
                component={GenericTable}
                allValues={allValues}
                change={change}
                eliminarRegistro={eliminarRegistro}
                currency={currency}
            />
        </div>
    );
};

export default FlujosInversionTable;
